import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { MessageType } from "src/components/notifications/Notifier";
import { authorizedFetch, isAbortError, toEndpointErrorMessage } from "src/network/authorizedFetch";
import { NetworkConfig } from "src/network/NetworkConfig";
import { IImportUser } from "src/stores/ImportUsersProgressModalStore";
import { RootStore } from "./RootStore";

/** Eine Schnittstelle des Kunden, etwa zu einem DMS. */
export interface ICustomerConnection {
    id: string;
    category: number;
    type: number;
    username: string | null;
}

/** Ein Systembenutzer des Kunden — der ERP-Benutzer oder der temporäre Admin. */
export interface ICustomerSystemUser {
    id: string;
    name: string;
    registered: boolean;
    roles: number[];
}

/** Eine Kundeneinstellung mit dem Zeitpunkt der letzten Aktualisierung. */
export interface ICustomerSetting {
    key: string | null;
    value: string | null;
    updated: string | null;
}

/** Ein Eintrag aus einer Übersetzungsliste, etwa die Benennung einer Rolle. */
export interface IEnumerationEntry {
    key: number;
    label: string | null;
}

/** Eine allgemeine Einstellung, hier die Mindest- und die aktuelle App-Version. */
export interface IGeneralSetting {
    key: string | null;
    value: string | null;
}

/** Ein Kopierauftrag für diesen Kunden, neueste zuerst. */
export interface ICustomerCopyJob {
    status: number;
    progressPercent: number;
    errorMessage: string | null;
    createdAt: string;
}

/** Ein laufender Benutzerimport des Kunden. */
export interface IUserImportProcess {
    id: string;
    state: number;
    workflowInstance: string | null;
    importData: { users?: IImportUser[] } | null;
}

/** Die Stammdaten des Kunden samt der Kennzahlen, die die Detailseite anzeigt. */
export interface ICustomerDetail {
    id: string;
    name: string | null;
    number: string | null;
    type: number;
    erp: number;
    erpcloud: boolean;
    active: boolean;
    apiVersion: number | null;
    dns: boolean;
    email: string | null;
    isCopied: boolean;
    partner: number;
    remarks: string | null;
    subdomain: string;
    features: number[];
    connections: ICustomerConnection[];
    erpUsers: ICustomerSystemUser[];
    adminUsers: ICustomerSystemUser[];
    internalUserCount: number;
    externalUserCount: number;
    realEstateCount: number;
    logCount: number;
    filesWithErrorCount: number;
    fileUsage: ICustomerSetting[];
}

/** Alles, was die Detailseite beim Öffnen anzeigt — eine Antwort des Endpoints. */
export interface ICustomerDetailPage {
    customer: ICustomerDetail;
    inspectionVersions: IGeneralSetting[];
    userRoles: IEnumerationEntry[];
    connectionTypes: IEnumerationEntry[];
    connectionCategories: IEnumerationEntry[];
    copyJobs: ICustomerCopyJob[];
    lastSentEmail: string | null;
    userImportProcesses: IUserImportProcess[];
}

export interface ICustomerDetailPageError {
    message: string;
}

/**
 * Die Antwort wird nur gecastet, nicht validiert. Fehlt darin eine Liste, zeigt die Seite sie
 * leer an, statt beim Rendern an `undefined.length` oder `undefined.map` zu scheitern.
 */
const withEmptyListsAsDefault = (detailPage: ICustomerDetailPage): ICustomerDetailPage => ({
    ...detailPage,
    customer: {
        ...detailPage.customer,
        features: detailPage.customer.features ?? [],
        connections: detailPage.customer.connections ?? [],
        erpUsers: detailPage.customer.erpUsers ?? [],
        adminUsers: detailPage.customer.adminUsers ?? [],
        fileUsage: detailPage.customer.fileUsage ?? []
    },
    inspectionVersions: detailPage.inspectionVersions ?? [],
    userRoles: detailPage.userRoles ?? [],
    connectionTypes: detailPage.connectionTypes ?? [],
    connectionCategories: detailPage.connectionCategories ?? [],
    copyJobs: detailPage.copyJobs ?? [],
    userImportProcesses: detailPage.userImportProcesses ?? []
});

/**
 * Lädt die Kundendetailseite über den Endpoint von Micro.Customer. Das Frontend holte diese
 * Angaben früher über vier einzelne Hasura-Queries, von denen zwei erst starteten, wenn die
 * erste zurück war — der Endpoint liefert alles in einer Antwort.
 */
export class CustomerDetailPageStore {
    rootStore: RootStore;

    @observable activeCustomerId: string | null = null;
    @observable.ref detailPage: ICustomerDetailPage | null = null;
    @observable loading = false;
    @observable.ref error: ICustomerDetailPageError | null = null;

    /**
     * Ein Wechsel auf einen anderen Kunden darf nicht von der Antwort des vorherigen
     * überschrieben werden.
     */
    private requestAbortController: AbortController | undefined = undefined;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this);
    }

    @computed
    get customer() {
        return this.detailPage?.customer;
    }

    @computed
    get copyJobs() {
        return this.detailPage?.copyJobs ?? [];
    }

    @computed
    get userImportProcess(): IUserImportProcess | undefined {
        return this.detailPage?.userImportProcesses[0];
    }

    @computed
    get lastSentEmail() {
        return this.detailPage?.lastSentEmail ?? null;
    }

    /**
     * Lädt die Detailseite des Kunden. Mit `silent` bleibt die angezeigte Seite stehen, statt
     * während der Anfrage den Ladebildschirm zu zeigen — und auch, wenn die Anfrage fehlschlägt:
     * Dann erscheint der Fehler nur als Meldung, statt die Seite durch den Fehlerbildschirm zu ersetzen.
     */
    load = async (customerId: string, { silent = false }: { silent?: boolean } = {}) => {
        this.abortRunningRequest();

        const abortController = new AbortController();
        this.requestAbortController = abortController;

        runInAction(() => {
            this.activeCustomerId = customerId;
            this.loading = !silent;
            this.error = null;
        });

        try {
            const detailPage = await authorizedFetch<ICustomerDetailPage>(
                this.rootStore.authStore,
                NetworkConfig.customerDetailPageUrl(customerId),
                "customer_detail_page",
                abortController.signal
            );

            runInAction(() => {
                this.detailPage = withEmptyListsAsDefault(detailPage);
            });
        } catch (error) {
            if (isAbortError(error)) {
                return;
            }

            const detailPageError = this.toDetailPageError(error);

            if (silent && this.detailPage !== null) {
                this.rootStore.uiStore.printStatusMessage(detailPageError.message, MessageType.ERROR);
                return;
            }

            runInAction(() => {
                this.detailPage = null;
                this.error = detailPageError;
            });
        } finally {
            if (this.requestAbortController === abortController) {
                this.requestAbortController = undefined;

                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    };

    /** Lädt den gerade angezeigten Kunden im Hintergrund neu, etwa nachdem seine Daten geändert wurden. */
    reload = async () => {
        if (this.activeCustomerId === null) {
            return;
        }

        await this.load(this.activeCustomerId, { silent: true });
    };

    @action
    reset = () => {
        this.abortRunningRequest();

        this.activeCustomerId = null;
        this.detailPage = null;
        this.loading = false;
        this.error = null;
    };

    private abortRunningRequest = () => {
        this.requestAbortController?.abort();
        this.requestAbortController = undefined;
    };

    private toDetailPageError = (error: unknown): ICustomerDetailPageError => ({
        message: toEndpointErrorMessage(error, "customer_detail_page")
    });
}
